/**
 * Skeleton — placeholder surface that previews the shape of content
 * still loading.
 *
 * Single responsibility: render a sized box (or a stack of lines) with
 * a soft shimmer so layouts do not collapse while data is in flight.
 * Honors `prefers-reduced-motion`: under reduced motion the shimmer
 * resolves to a static low-contrast wash.
 *
 * The companion {@link SkeletonText} renders a typed cluster of lines
 * with a configurable last-line width — the canonical "paragraph
 * placeholder" pattern.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useEffect, useMemo } from "react";
import { GRAY } from "../design-system/tokens.js";
import { cn } from "../styling/index.js";

export type SkeletonShape = "rect" | "circle" | "line";

export interface SkeletonProps {
  /** Geometry; defaults to `rect`. */
  readonly shape?: SkeletonShape;
  /** CSS width — number is treated as pixels. */
  readonly width?: number | string;
  /** CSS height — number is treated as pixels. Defaults to `1rem`. */
  readonly height?: number | string;
  /** Accessible label. Defaults to "Loading". */
  readonly ariaLabel?: string;
  /** Additional className appended to the placeholder. */
  readonly className?: string;
  /**
   * When true, the placeholder renders without ARIA live-region
   * semantics. `SkeletonText` uses this to avoid nesting one live
   * region per line inside its outer live region.
   */
  readonly presentational?: boolean;
}

const KEYFRAMES_ID = "csk-demo-ui-skeleton-shimmer-keyframes";

function ensureKeyframes(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(KEYFRAMES_ID) !== null) return;
  const style = document.createElement("style");
  style.id = KEYFRAMES_ID;
  style.textContent =
    "@keyframes csk-skeleton-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } } " +
    "@media (prefers-reduced-motion: reduce) { [data-csk-skeleton-shape] { animation: none !important; } }";
  document.head.appendChild(style);
}

function sizeValue(value: number | string | undefined, fallback: string): string {
  if (value === undefined) return fallback;
  if (typeof value === "number") return `${value}px`;
  return value;
}

function shapeStyle(shape: SkeletonShape): React.CSSProperties {
  if (shape === "circle") return { borderRadius: "9999px" };
  if (shape === "line") return { borderRadius: "0.25rem" };
  return { borderRadius: "0.5rem" };
}

/** Renders a single shimmering placeholder surface. */
export const Skeleton = React.memo(function Skeleton({
  shape = "rect",
  width,
  height,
  ariaLabel = "Loading",
  className,
  presentational = false,
}: SkeletonProps): React.JSX.Element {
  useEffect(() => {
    ensureKeyframes();
  }, []);

  const computedWidth = sizeValue(width, "100%");
  const computedHeight = sizeValue(height, shape === "line" ? "0.75rem" : "1rem");
  const style = useMemo<React.CSSProperties>(
    () => ({
      width: computedWidth,
      height: computedHeight,
      background: `linear-gradient(90deg, ${GRAY[300]} 25%, ${GRAY[100]} 50%, ${GRAY[300]} 75%)`,
      backgroundSize: "200% 100%",
      animation: "csk-skeleton-shimmer 1.4s ease-in-out infinite",
      ...shapeStyle(shape),
    }),
    [computedHeight, computedWidth, shape],
  );
  const liveRegionProps = presentational
    ? ({ "aria-hidden": "true" } as const)
    : ({ role: "status", "aria-live": "polite", "aria-label": ariaLabel } as const);

  return (
    <span
      {...liveRegionProps}
      data-csk-skeleton-shape={shape}
      className={cn("inline-block align-middle", className)}
      style={style}
    />
  );
});

export interface SkeletonTextProps {
  /** Number of lines to render. Defaults to 3. */
  readonly lines?: number;
  /**
   * Width of the final line as a CSS string (e.g. `"60%"`). Defaults
   * to `"40%"` so the cluster reads as the end of a paragraph.
   */
  readonly lastLineWidth?: string;
  /** Accessible label. Defaults to "Loading text". */
  readonly ariaLabel?: string;
  /** Additional className appended to the wrapper. */
  readonly className?: string;
}

/** Renders a stack of skeleton lines that suggest a paragraph shape. */
export const SkeletonText = React.memo(function SkeletonText({
  lines = 3,
  lastLineWidth = "40%",
  ariaLabel = "Loading text",
  className,
}: SkeletonTextProps): React.JSX.Element {
  const safeLines = Math.max(1, Math.trunc(lines));
  const indexes = useMemo(
    () => Array.from({ length: safeLines }, (_, index) => index),
    [safeLines],
  );
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={ariaLabel}
      className={cn("flex flex-col gap-2", className)}
    >
      {indexes.map((index) => {
        const isLast = index === safeLines - 1;
        const width = isLast && safeLines > 1 ? lastLineWidth : "100%";
        return <Skeleton key={index} shape="line" width={width} height="0.75rem" presentational />;
      })}
    </div>
  );
});
